/**
 * The usage ledger (mission 9 WP13 T083; sheet 50 callouts 11 and 12; B.3, B.4, GEN.4, TX.8, U.2, ANA.13;
 * FR-012; NFR-011, NFR-012; amendment 6; research §1.20; contracts/ui-api.md "Sheet 50").
 *
 * `ledgerQuery({include: 'free'})`: page 1 on mount and older pages only on demand, `ledgerOlder`, while
 * the page carries a cursor (NFR-012). Newest first, each row names its run or billing event, the signed
 * credits and the balance after it:
 * - `cycle_grant` → `ledgerGrant`; `purchase` → `ledgerPurchase`; `forgiveness` → `ledgerForgiveness`;
 * - `expiry` → `ledgerExpiryIncluded`, or `ledgerExpiryBlock` when it names a block;
 * - `reservation` → `ledgerRun`, with its allocation (`ledgerAllocation`) beneath;
 * - `release` → `ledgerRunReturned`; `consumption`, the run's 0-delta finalizer written at ready on both
 *   paths, → `ledgerRunCompleted` with no suffix. On Path B the analysis is the charge, and the entry carries
 *   no path, so no consumption row claims a free analysis (WP13 review cycle 1, orchestrator ruling (a);
 *   FR-012, B.7, research §1.20);
 * - the free items at 0 (`free_upload` → `ledgerFreeUpload`, `free_transmission` →
 *   `ledgerFreeTransmission`, and mission 9 WP17's `free_analysis`, a Path A run's analysis of the file
 *   Trellis generated, → `ledgerRun` with the `ledgerAnalysisFree` suffix, its own row and never a suffix on a
 *   consumption row; FR-012, WP13 ruling), tagged `ledgerFree`, so the reporter sees what was not charged.
 *
 * Dates, ids, credits and balances in mono; times in UTC, stated once. The table's container scrolls
 * horizontally at 1280 px, never the page (NFR-011). States: `loading`, `empty`, `page`,
 * `older-loading`, `free-items`, `error`.
 */
import { useInfiniteQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import type { LedgerEntry } from '@/lib/api/credits'
import { Mono, count, date, shortId, time } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { ledgerQuery } from '@/lib/queries'

import { BillingProblem } from './BillingProblem'

const COLUMNS = 4

/** The movement's words: its run or billing event (FR-012). */
function movement(entry: LedgerEntry): string {
  const run = shortId(entry.run_id ?? '')
  switch (entry.kind) {
    case 'cycle_grant':
      return copyText('ledgerGrant')
    case 'purchase':
      return copyText('ledgerPurchase')
    case 'forgiveness':
      return copyText('ledgerForgiveness')
    case 'expiry':
      return copyText(entry.block_id === null ? 'ledgerExpiryIncluded' : 'ledgerExpiryBlock')
    case 'reservation':
      return PROVISIONAL_COPY.ledgerRun.render({ run })
    case 'release':
      return PROVISIONAL_COPY.ledgerRunReturned.render({ run })
    case 'consumption':
      return PROVISIONAL_COPY.ledgerRunCompleted.render({ run })
    case 'free_upload':
      return copyText('ledgerFreeUpload')
    case 'free_transmission':
      return PROVISIONAL_COPY.ledgerFreeTransmission.render({ run })
    case 'free_analysis':
      return `${PROVISIONAL_COPY.ledgerRun.render({ run })} · ${copyText('ledgerAnalysisFree')}`
  }
}

/** A reservation's sources, summed: included this cycle and purchased blocks. */
function allocation(entry: LedgerEntry): string | null {
  if (entry.allocation === undefined || entry.allocation.length === 0) return null
  const sum = (source: 'included' | 'block') =>
    entry.allocation?.filter((a) => a.source === source).reduce((total, a) => total + a.credits, 0) ?? 0
  return PROVISIONAL_COPY.ledgerAllocation.render({ included: sum('included'), purchased: sum('block') })
}

const signed = (delta: number) => (delta > 0 ? `+${count(delta)}` : count(delta))

const isFree = (entry: LedgerEntry) => entry.kind === 'free_upload' || entry.kind === 'free_transmission' || entry.kind === 'free_analysis'

function Row({ entry }: { entry: LedgerEntry }) {
  const split = allocation(entry)
  return (
    <TableRow dense>
      <TableCell className="whitespace-nowrap">
        <Mono className="text-body-sm">
          <time dateTime={entry.created_at}>
            {date(entry.created_at)} {time(entry.created_at)}
          </time>
        </Mono>
      </TableCell>
      <TableCell>
        <span>{movement(entry)}</span>
        {isFree(entry) ? (
          <span className="ml-2 inline-flex h-[22px] items-center rounded-sm border border-navy-200 bg-navy-50 px-2 text-caption font-medium text-navy-700">
            {copyText('ledgerFree')}
          </span>
        ) : null}
        {split === null ? null : <span className="block text-caption text-navy-600">{split}</span>}
      </TableCell>
      <TableCell numeric>{signed(entry.delta)}</TableCell>
      <TableCell numeric>{count(entry.balance_after)}</TableCell>
    </TableRow>
  )
}

export function UsageLedger() {
  const ledger = useInfiniteQuery(ledgerQuery({ include: 'free' }))
  const entries = ledger.data?.pages.flatMap((page) => page.entries) ?? []
  const loading = ledger.isPending

  let body
  if (ledger.isError && entries.length === 0) {
    body = <BillingProblem error={ledger.error} />
  } else if (!loading && entries.length === 0) {
    body = <p className="text-body-sm text-navy-600">{copyText('zeroListed')}</p>
  } else {
    body = (
      <>
        <Table aria-busy={loading || undefined}>
          <TableCaption>{copyText('timesInUtc')}</TableCaption>
          <TableHeader>
            <TableRow dense>
              <TableHead>{copyText('ledgerColDate')}</TableHead>
              <TableHead>{copyText('ledgerColMovement')}</TableHead>
              <TableHead className="text-right">{copyText('ledgerColCredits')}</TableHead>
              <TableHead className="text-right">{copyText('ledgerColBalance')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableSkeletonRows columns={COLUMNS} /> : entries.map((entry) => <Row key={`${entry.kind}:${entry.id}`} entry={entry} />)}
          </TableBody>
        </Table>
        {ledger.isFetchNextPageError ? <BillingProblem error={ledger.error} /> : null}
        {ledger.hasNextPage ? (
          <div>
            <Button variant="secondary" size="sm" loading={ledger.isFetchingNextPage} onClick={() => void ledger.fetchNextPage()}>
              {copyText('ledgerOlder')}
            </Button>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{copyText('ledgerTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{body}</CardContent>
    </Card>
  )
}
